import {
  ArrowLeft,
  Bell,
  HelpCircle,
  Info,
  type LucideIcon,
  MoreVertical,
  Pencil,
  Search,
  Share,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  BookmarkFilledIcon,
  BookmarkIcon,
  EyeIcon,
  PencilIcon,
  SettingIcon,
  UploadIcon,
} from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useUnreadCount } from "@/features/notifications/hooks/useNotifications"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import { getAssetUrl } from "@/utils/assets"

type TopNavVariant = "main" | "back-more" | "my-rituality"

export interface DropdownAction {
  label: string
  icon?: LucideIcon
  onClick: () => void
  className?: string
}

export interface InlineAction {
  icon: "share" | "save" | "info" | "ritualEdit"
  onClick: () => void
  active?: boolean
}

export interface MobileTopNavProps {
  variant: TopNavVariant
  onBack?: () => void
  onMore?: () => void
  onSearch?: () => void
  onNotifications?: () => void
  onShare?: () => void
  onHamburgerClick?: () => void
  moreActions?: DropdownAction[]
  inlineActions?: InlineAction[]
  centerLabel?: React.ReactNode
  helpCenterAction?: () => void
}

export function MobileTopNav({
  variant,
  onBack,
  onMore,
  onSearch,
  onNotifications,
  onShare,
  onHamburgerClick,
  moreActions,
  inlineActions,
  centerLabel,
  helpCenterAction,
}: MobileTopNavProps) {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header
      className={cn(
        "sticky top-0 left-0 right-0 z-50 h-[48px] transition-all duration-300 md:hidden",
        isScrolled ? "bg-[#020202]/80 backdrop-blur-lg shadow-lg" : "bg-transparent",
      )}
    >
      <div className="flex items-center justify-between px-5 h-full pt-[env(safe-area-inset-top)]">
        {variant === "main" ? (
          <MainNavContent
            onSearch={onSearch}
            onNotifications={onNotifications}
            onHamburgerClick={onHamburgerClick}
          />
        ) : variant === "my-rituality" ? (
          <MyRitualityNavContent onShare={onShare} />
        ) : (
          <BackMoreNavContent
            onBack={onBack}
            onMore={onMore}
            moreActions={moreActions}
            inlineActions={inlineActions}
            centerLabel={centerLabel}
            helpCenterAction={helpCenterAction}
          />
        )}
      </div>
    </header>
  )
}

function MainNavContent({
  onSearch,
  onNotifications,
  onHamburgerClick,
}: {
  onSearch?: () => void
  onNotifications?: () => void
  onHamburgerClick?: () => void
}) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const logoWordmark = getAssetUrl("auth-logo.png")
  const hamburger = getAssetUrl("hambuger_icon.svg")
  const { data: unreadCount } = useUnreadCount()

  const userImageUrl =
    user?.profile_photos?.find(p => p.is_cover_image || p.isCover)?.filePath || undefined
  const defaultAvatarUrl = getAssetUrl("upload-bg.jpeg")

  return (
    <>
      <div className="flex justify-center items-center gap-5">
        <button onClick={onHamburgerClick} type="button">
          <img src={hamburger} className="flex items-center justify-center" />
        </button>
        <img
          src={logoWordmark}
          alt={t("notifications.brandName")}
          className="h-[21px] pb-1.25 cursor-pointer"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onSearch}
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
        >
          <Search size={22} />
        </button>
        <button
          onClick={onNotifications}
          className="relative flex items-center justify-center w-[36px] h-[36px] text-primary"
        >
          <Bell size={22} />
          {(unreadCount || 0) > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red text-white text-[10px] font-semibold rounded-full flex items-center justify-center px-1">
              {(unreadCount || 0) > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
        <button onClick={() => navigate("/profile")} className="ml-1">
          <Avatar
            src={userImageUrl}
            fallback={user?.name || user?.email || "User"}
            fallbackSrc={defaultAvatarUrl}
            size="md"
          />
        </button>
      </div>
    </>
  )
}

function BackMoreNavContent({
  onBack,
  onMore,
  moreActions,
  inlineActions,
  centerLabel,
  helpCenterAction,
}: {
  onBack?: () => void
  onMore?: () => void
  moreActions?: DropdownAction[]
  inlineActions?: InlineAction[]
  centerLabel?: React.ReactNode
  helpCenterAction?: () => void
}) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const defaultHelpCenterAction = () => window.open("mailto:support@myrituality.com", "_self")
  const handleHelpCenter = helpCenterAction || defaultHelpCenterAction

  return (
    <>
      <button
        onClick={onBack || (() => navigate(-1))}
        className="flex items-center justify-center w-[44px] h-[44px] -ml-2 text-primary"
      >
        <ArrowLeft size={24} />
      </button>
      {centerLabel && <div className="flex-1 flex justify-center">{centerLabel}</div>}
      <div className="flex items-center gap-1">
        {inlineActions?.map((action, i) =>
          action.icon === "share" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label={t("mobileNav.share")}
            >
              <Share size={22} />
            </button>
          ) : action.icon === "save" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label={t("mobileNav.save")}
            >
              {action.active ? (
                <BookmarkFilledIcon size={22} className="fill-current" />
              ) : (
                <BookmarkIcon size={22} />
              )}
            </button>
          ) : action.icon === "info" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label={t("mobileNav.tips")}
            >
              <Info size={22} />
            </button>
          ) : action.icon === "ritualEdit" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label={t("mobileNav.edit")}
            >
              <Pencil size={22} />
            </button>
          ) : null,
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={onMore}
              className="flex items-center justify-center w-[44px] h-[44px] -mr-2 text-primary"
            >
              <MoreVertical size={24} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {moreActions?.map((action, i) => (
              <DropdownMenuItem key={i} onClick={action.onClick} className={action.className}>
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </DropdownMenuItem>
            ))}
            {moreActions && moreActions.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={handleHelpCenter}>
              <HelpCircle className="size-5" />
              Help Center
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}

function MyRitualityNavContent({ onShare }: { onShare?: () => void }) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)

  const handleShare = () => {
    if (onShare) {
      onShare()
    }
  }

  return (
    <>
      <button
        type="button"
        className="flex items-center justify-center w-[44px] h-[44px] -ml-2 text-primary"
        onClick={() => navigate("/profile/edit")}
        aria-label={t("mobileNav.editProfile")}
      >
        <PencilIcon className="!w-6 !h-6" />
      </button>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
          onClick={() => navigate("/settings")}
          aria-label={t("mobileNav.settings")}
        >
          <SettingIcon className="!w-6 !h-6" />
        </button>
        <button
          type="button"
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
          onClick={handleShare}
          aria-label={t("mobileNav.share")}
        >
          <UploadIcon className="!w-6 !h-6" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label={t("mobileNav.more")}
            >
              <MoreVertical className="w-6 h-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => user?.id && navigate(`/profile/${user.id}`)}>
              <EyeIcon className="size-5" />
              See Public Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings?section=help-center")}>
              <HelpCircle className="size-5" />
              Help Center
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}
